import { useCallback, useEffect, useMemo, useState } from 'react';
import { getCashSummary } from '../../infra/cashSummaryQuery';
import type { CashSummary } from '../../infra/cashSummaryQuery';
import { resolveExpectedCashInDrawer } from '../../infra/cashSummaryTypes';
import { resolveCashSummary } from '../../infra/resolveCashSummary';
import {
  resolveCashSummaryRefreshOptions,
  getCachedRemoteCashSummary,
} from '../../infra/resolveCashSummary';
import {
  subscribeCashSessionChangeSignalDebounced,
} from '../../infra/cashSessionChangeSignal';
import { captureCatch } from '../../../shared/sentry/sentryUser';

type Params = {
  currentBranchId: string | null | undefined;
};

export function useCashKpis({ currentBranchId }: Params) {
  const [cashSummary, setCashSummary] = useState<CashSummary | null>(() => {
    if (!currentBranchId) return null;
    return getCachedRemoteCashSummary(currentBranchId) ?? null;
  });
  const [loading, setLoading] = useState(() => Boolean(currentBranchId));
  const [initialLoading, setInitialLoading] = useState(() => Boolean(currentBranchId));

  // ADR-017 Fase 4: sin SQLite local — el servidor es la única autoridad.
  const applyCashSummaryState = useCallback((_local: CashSummary, merged: CashSummary) => {
    setCashSummary(merged);
  }, []);

  const refreshCashSummary = useCallback(
    async (options?: { force?: boolean }) => {
      if (!currentBranchId) {
        setCashSummary(null);
        return;
      }
      try {
        const local = await getCashSummary(currentBranchId);
        if (!options?.force) {
          applyCashSummaryState(local, local);
        }
        const resolveOptions = await resolveCashSummaryRefreshOptions(local, options);
        const merged = await resolveCashSummary(currentBranchId, resolveOptions);
        applyCashSummaryState(local, merged);
      } catch (e) {
        captureCatch('cash', 'useCashKpis.refreshCashSummary')(e);
      }
    },
    [applyCashSummaryState, currentBranchId]
  );

  const prepareCloseCashModal = useCallback(async () => {
    await refreshCashSummary({ force: true });
  }, [refreshCashSummary]);

  useEffect(() => {
    if (currentBranchId) {
      setInitialLoading(true);
      setLoading(true);
    } else {
      setInitialLoading(false);
      setLoading(false);
      setCashSummary(null);
    }
  }, [currentBranchId]);

  const loadFn = useCallback(async () => {
    if (!currentBranchId) {
      setLoading(false);
      setInitialLoading(false);
      setCashSummary(null);
      return;
    }
    setLoading(true);
    try {
      await refreshCashSummary();
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [currentBranchId, refreshCashSummary]);

  useEffect(() => {
    return subscribeCashSessionChangeSignalDebounced(() => {
      void refreshCashSummary();
    });
  }, [refreshCashSummary]);

  const expectedCashInDrawer = useMemo(
    () => resolveExpectedCashInDrawer(cashSummary),
    [cashSummary],
  );

  return {
    cashSummary,
    loading,
    initialLoading,
    applyCashSummaryState,
    refreshCashSummary,
    prepareCloseCashModal,
    expectedCashInDrawer,
    loadFn,
  };
}
